import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CalendarDays, ChevronDown, ChevronRight } from 'lucide-react'
import AdminLayout from '../components/AdminLayout'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

const formatMoney = (value: unknown, currency = '£') => {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return '—'
  const formatted = Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return num < 0 ? `(${currency}${formatted})` : `${currency}${formatted}`
}

const formatNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return '—'
  return num.toLocaleString()
}

const formatBool = (value: unknown) => {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return '—'
}

const titleCase = (value?: string | null) =>
  value
    ? value
        .toString()
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .replace(/[_-]/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '—'

const statusToneClass = (status: string) => {
  const v = status.toLowerCase()
  if (['submitted', 'completed', 'accepted', 'successful'].includes(v)) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-100'
  }
  if (v.includes('draft') || v.includes('pending') || v.includes('progress')) {
    return 'bg-amber-50 text-amber-700 border-amber-100'
  }
  return 'bg-red-50 text-red-700 border-red-100'
}

type FieldProps = {
  label: string
  value: React.ReactNode
  full?: boolean
}

const Field = ({ label, value, full = false }: FieldProps) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
    <p className='mt-0.5 text-sm text-gray-900 break-words'>
      {value === '' || value === null || value === undefined ? '—' : value}
    </p>
  </div>
)

type SectionProps = {
  title: string
  subtitle?: string
  children: React.ReactNode
  defaultOpen?: boolean
  collapsible?: boolean
}

const Section = ({ title, subtitle, children, defaultOpen = true, collapsible = true }: SectionProps) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className='rounded-2xl bg-white border border-gray-100 shadow-sm'>
      <button
        type='button'
        onClick={() => collapsible && setOpen(o => !o)}
        className='w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-gray-100 text-left'
      >
        <div>
          <p className='text-sm sm:text-base font-semibold text-gray-900'>{title}</p>
          {subtitle && <p className='text-xs text-gray-500'>{subtitle}</p>}
        </div>
        {collapsible &&
          (open ? <ChevronDown className='h-4 w-4 text-gray-500' /> : <ChevronRight className='h-4 w-4 text-gray-500' />)}
      </button>
      {open && <div className='px-4 sm:px-5 py-4 sm:py-5'>{children}</div>}
    </div>
  )
}

const renderMoneyRows = (rows: Array<{ label: string; value: unknown; emphasize?: boolean }>) => (
  <div className='divide-y divide-gray-100'>
    {rows.map(row => (
      <div
        key={row.label}
        className={`flex items-center justify-between gap-4 py-2 text-sm ${
          row.emphasize ? 'font-semibold text-gray-900' : 'text-gray-700'
        }`}
      >
        <span className='text-left'>{row.label}</span>
        <span className='text-right whitespace-nowrap'>{formatMoney(row.value)}</span>
      </div>
    ))}
  </div>
)

export default function AdminFilingDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [filing, setFiling] = useState<any | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [showXml, setShowXml] = useState<Record<number, boolean>>({})

  useEffect(() => {
    const loadFiling = async () => {
      if (!id) return
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.getFiling(id)
        const data: any = (response as any)?.data || response
        setFiling(data?.filing || data)
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load filing details.')
      } finally {
        setLoading(false)
      }
    }
    void loadFiling()
  }, [id])

  const raw = filing?.raw || {}
  const sections = raw.sections || {}
  const companyInfo = sections.companyInformation || {}
  const financial = sections.financialData?.statement || {}
  const balance = sections.balanceSheet?.statement || {}
  const taxComp = sections.taxComputation?.computation || {}
  const taxCalc = sections.taxCalculation?.liabilities || {}
  const rAndD = sections.researchAndDevelopment || {}
  const closeCompany = sections.closeCompany || {}
  const schedules = sections.supplementarySchedules?.schedules || {}
  const declaration = sections.declaration?.declaration || {}
  const xmlAuditLog: any[] = Array.isArray(raw.xmlAuditLog) ? raw.xmlAuditLog : []
  const submission = filing?.submissionDetails || {}
  const accountingPeriod = companyInfo.accountingPeriod || {
    start: filing?.accountingPeriodStart,
    end: filing?.accountingPeriodEnd,
  }
  const registeredOffice = companyInfo.registeredOffice || {}
  const contact = companyInfo.contact || {}
  const flags = companyInfo.flags || {}
  const ixbrl = companyInfo.ixbrlFiling || {}

  const statusRaw = (filing?.status || filing?.filingStatus || '').toString()
  const statusLabel = titleCase(statusRaw) || '—'

  const submittedByName = useMemo(() => {
    const sb: any = filing?.submittedBy
    const fromSubmission = submission.submittedBy
    if (sb?.name) return sb.name
    if (sb?.firstName || sb?.lastName) return [sb?.firstName, sb?.lastName].filter(Boolean).join(' ')
    if (sb?.email) return sb.email
    if (fromSubmission) return fromSubmission
    return '—'
  }, [filing, submission.submittedBy])

  const taxpayer = filing?.companyName || raw.companyName || companyInfo.companyName || '—'

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>Filing Details</h1>
            <p className='text-sm text-gray-500'>Review the full record of this filing</p>
          </div>
          <div className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm text-gray-700 shadow-sm self-start sm:self-auto'>
            <CalendarDays className='h-4 w-4 text-gray-500 flex-shrink-0' />
            <span className='whitespace-nowrap'>15 Jun - 21 Jun 2025</span>
          </div>
        </div>

        <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5'>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2 min-w-0'>
              <button
                onClick={() => navigate(-1)}
                className='h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 hover:text-gray-900 flex-shrink-0'
              >
                ←
              </button>
              <p className='text-base sm:text-lg font-semibold text-gray-900 truncate'>
                {filing?.type || filing?.filingType || 'Filing'} — {taxpayer}
              </p>
            </div>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${statusToneClass(
                statusRaw,
              )}`}
            >
              {statusLabel}
            </span>
          </div>
        </div>

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        {loading ? (
          <div className='rounded-2xl border border-gray-100 bg-white shadow-sm p-6 text-sm text-gray-700'>
            Loading filing details...
          </div>
        ) : (
          <>
            <Section title='Filing Overview'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Filing ID' value={filing?.id || filing?._id || id} />
                <Field label='Type' value={filing?.type || filing?.filingType} />
                <Field label='Status' value={statusLabel} />
                <Field label='Form Version' value={raw.formVersion} />
                <Field label='Version' value={raw.version} />
                <Field label='Plan ID' value={raw.planId} />
                <Field label='Filing Date' value={formatDateTime(filing?.filingDate)} />
                <Field label='Last Updated' value={formatDateTime(filing?.lastUpdated)} />
                <Field label='Last Reminder' value={formatDateTime(filing?.lastReminder)} />
                <Field
                  label='Accounting Period'
                  value={`${formatDate(accountingPeriod.start)} – ${formatDate(accountingPeriod.end)}`}
                />
                <Field label='Created By (User ID)' value={raw.createdBy} />
                <Field label='Company ID' value={filing?.companyId || raw.companyId} />
              </div>
            </Section>

            <Section title='Submission Details'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Submitted By' value={submission.submittedBy || submittedByName} />
                <Field label='Submitter Role' value={titleCase(submission.submitterRole)} />
                <Field label='Declaration Accepted' value={formatBool(submission.declarationAccepted)} />
                <Field label='HMRC Acknowledgement' value={titleCase(submission.hmrcAcknowledgement)} />
                <Field label='Submission Reference' value={submission.submissionReference} />
                <Field label='Correlation ID' value={submission.correlationId} />
                <Field label='Submission Timestamp' value={formatDateTime(submission.submissionTimestamp)} />
                <Field label='Requires Polling' value={formatBool(submission.requiresPolling)} />
                <Field label='Last Polled At' value={formatDateTime(submission.lastPolledAt)} />
                <Field label='Gov Gateway User ID' value={submission.governmentGatewayUserId} />
                <Field label='Gov Gateway Password' value={submission.governmentGatewayPasswordStored ? 'Stored (encrypted)' : '—'} />
                <Field label='Software Name' value={submission.softwareDetails?.name} />
                <Field label='Software Version' value={submission.softwareDetails?.version} />
                <Field label='Software Vendor' value={submission.softwareDetails?.vendor} />
              </div>
            </Section>

            <Section title='Submitter'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Name' value={submittedByName} />
                <Field label='Email' value={filing?.submittedBy?.email} />
                <Field label='Phone' value={filing?.submittedBy?.phoneNumber || filing?.submittedBy?.phone} />
                <Field label='User ID' value={filing?.submittedBy?.id || filing?.submittedBy?._id} />
              </div>
            </Section>

            <Section title='Company Information'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Company Name' value={companyInfo.companyName || raw.companyName} />
                <Field label='Trading Name' value={companyInfo.tradingName} />
                <Field label='Company Number' value={companyInfo.companyRegistrationNumber || raw.companyNumber} />
                <Field label='UTR' value={companyInfo.utr || raw.utr} />
                <Field label='Tax Reference' value={companyInfo.taxReference} />
                <Field label='HMRC Office Code' value={companyInfo.hmrcOfficeCode} />
                <Field label='Return Type' value={titleCase(companyInfo.returnType)} />
                <Field label='Reason For Return' value={companyInfo.reasonForReturn} />
                <Field label='First Return' value={formatBool(companyInfo.firstReturn)} />
                <Field label='Final Return' value={formatBool(companyInfo.finalReturn)} />
                <Field label='Amended Return' value={formatBool(companyInfo.amendedReturn)} />
                <Field label='Amendment Reason' value={companyInfo.amendmentReason} />
                <Field label='Associated Companies' value={formatNumber(companyInfo.associatedCompanies)} />
                <Field label='Dormant' value={formatBool(flags.isDormant)} />
                <Field label='Close Company' value={formatBool(flags.isCloseCompany)} />
                <Field label='Investment Company' value={formatBool(flags.isInvestmentCompany)} />
              </div>
            </Section>

            <Section title='Registered Office & Contact'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Address Line 1' value={registeredOffice.addressLine1 || registeredOffice.line1} />
                <Field label='Address Line 2' value={registeredOffice.addressLine2 || registeredOffice.line2} />
                <Field label='City / Town' value={registeredOffice.town || registeredOffice.city} />
                <Field label='County' value={registeredOffice.county} />
                <Field label='Postcode' value={registeredOffice.postCode || registeredOffice.postcode} />
                <Field label='Country' value={registeredOffice.country} />
                <Field label='Contact Name' value={contact.name} />
                <Field label='Contact Phone' value={contact.phone} />
                <Field label='Contact Email' value={contact.email} />
              </div>
            </Section>

            <Section title='iXBRL Filing'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Legal Form' value={ixbrl.legalFormEntity} />
                <Field label='Principal Activities' value={ixbrl.principalActivities} />
                <Field label='Accounts Status' value={ixbrl.accountsStatus} />
                <Field label='Accounts Type' value={ixbrl.accountsType} />
                <Field label='Accounting Standards' value={ixbrl.accountingStandards} />
                <Field label='Director Approving Accounts' value={ixbrl.directorApprovingAccounts} />
                <Field label='Balance Sheet Approval Date' value={formatDate(ixbrl.balanceSheetApprovalDate)} />
                <Field label='Average Employees' value={formatNumber(ixbrl.averageEmployees)} />
                <Field label='Auditor Name' value={ixbrl.auditorName} />
              </div>
            </Section>

            <Section title='Profit & Loss Account' subtitle={sections.financialData?.title}>
              {renderMoneyRows([
                { label: 'Turnover', value: financial.turnover },
                { label: 'Other Income', value: financial.otherIncome },
                { label: 'Total Income', value: financial.totalIncome, emphasize: true },
                { label: 'Cost of Sales', value: financial.costOfSales },
                { label: 'Gross Profit', value: financial.grossProfit, emphasize: true },
                { label: 'Distribution Costs', value: financial.distributionCosts },
                { label: 'Administrative Expenses', value: financial.administrativeExpenses },
                { label: 'Other Operating Income', value: financial.otherOperatingIncome },
                { label: 'Operating Profit', value: financial.operatingProfit, emphasize: true },
                { label: 'Interest Receivable', value: financial.interestReceivable },
                { label: 'Interest Payable', value: financial.interestPayable },
                { label: 'Profit Before Tax', value: financial.profitBeforeTax, emphasize: true },
                { label: 'UK Tax On Profits', value: financial.ukTaxOnProfits },
                { label: 'Overseas Tax On Profits', value: financial.overseasTaxOnProfits },
                { label: 'Tax Expense', value: financial.taxExpense },
                { label: 'Profit After Tax', value: financial.profitAfterTax, emphasize: true },
              ])}
            </Section>

            <Section title='Balance Sheet' subtitle={sections.balanceSheet?.title}>
              {renderMoneyRows([
                { label: 'Intangible Assets', value: balance.intangibleAssets },
                { label: 'Tangible Assets', value: balance.tangibleAssets },
                { label: 'Investments', value: balance.investments },
                { label: 'Total Fixed Assets', value: balance.totalFixedAssets, emphasize: true },
                { label: 'Stocks', value: balance.stocks },
                { label: 'Debtors', value: balance.debtors },
                { label: 'Cash at Bank and in Hand', value: balance.cashAtBankAndInHand },
                { label: 'Total Current Assets', value: balance.totalCurrentAssets, emphasize: true },
                { label: 'Creditors Due Within One Year', value: balance.creditorsDueWithinOneYear },
                { label: 'Net Current Assets', value: balance.netCurrentAssets, emphasize: true },
                { label: 'Total Assets Less Current Liabilities', value: balance.totalAssetsLessCurrentLiabilities, emphasize: true },
                { label: 'Creditors Due After One Year', value: balance.creditorsDueAfterOneYear },
                { label: 'Provisions', value: balance.provisions },
                { label: 'Net Assets', value: balance.netAssets, emphasize: true },
                { label: 'Called Up Share Capital', value: balance.calledUpShareCapital },
                { label: 'Share Premium', value: balance.sharePremium },
                { label: 'Revaluation Reserve', value: balance.revaluationReserve },
                { label: 'Other Reserves', value: balance.otherReserves },
                { label: 'Profit & Loss Account', value: balance.profitAndLossAccount },
                { label: 'Shareholders Funds', value: balance.shareholdersFunds, emphasize: true },
                { label: 'Opening Profit/Loss', value: balance.openingProfitLoss },
              ])}
            </Section>

            <Section title='Tax Adjustments' subtitle={sections.taxComputation?.title}>
              {renderMoneyRows([
                { label: 'Profit/Loss Per Accounts', value: taxComp.profitLossPerAccounts },
                { label: 'Depreciation', value: taxComp.depreciation },
                { label: 'Entertainment Disallowable', value: taxComp.entertainmentDisallowable },
                { label: 'Provisions Not Allowable', value: taxComp.provisionsNotAllowable },
                { label: 'Other Disallowable Expenses', value: taxComp.otherDisallowableExpenses },
                { label: 'Capital Allowances', value: taxComp.capitalAllowances },
                { label: 'Profit on Sale of Assets', value: taxComp.profitOnSaleOfAssets },
                { label: 'Other Adjustments', value: taxComp.otherAdjustments },
                { label: 'Trading Profit/Loss', value: taxComp.tradingProfitLoss, emphasize: true },
                { label: 'Bank Interest', value: taxComp.bankInterest },
                { label: 'Rental Income', value: taxComp.rentalIncome },
                { label: 'Dividends Received', value: taxComp.dividendsReceived },
                { label: 'Capital Gains', value: taxComp.capitalGains },
                { label: 'Other Income', value: taxComp.otherIncome },
                { label: 'Total Profits', value: taxComp.totalProfits, emphasize: true },
                { label: 'Losses Brought Forward', value: taxComp.lossesBroughtForward },
                { label: 'Losses This Period', value: taxComp.lossesThisPeriod },
                { label: 'Charitable Donations', value: taxComp.charitableDonations },
                { label: 'Taxable Profit After Losses', value: taxComp.taxableProfitAfterLosses, emphasize: true },
                { label: 'Taxable Profit After Charitable Donations', value: taxComp.taxableProfitAfterCharitableDonations, emphasize: true },
              ])}
            </Section>

            <Section title='Corporation Tax Calculation' subtitle={sections.taxCalculation?.title}>
              {renderMoneyRows([
                { label: 'Taxable Profit for CT', value: taxCalc.taxableProfitForCT },
                { label: 'Main Rate Profit', value: taxCalc.mainRateProfit },
                { label: 'Main Rate Tax', value: taxCalc.mainRateTax },
                { label: 'Small Profits Rate Profit', value: taxCalc.smallProfitsRateProfit },
                { label: 'Small Profits Rate Tax', value: taxCalc.smallProfitsRateTax },
                { label: 'Marginal Rate Relief', value: taxCalc.marginalRateRelief },
                { label: 'Corporation Tax Liability', value: taxCalc.corporationTaxLiability, emphasize: true },
                { label: 'Double Taxation Relief', value: taxCalc.doubleTaxationRelief },
                { label: 'Additional Tax Relief', value: taxCalc.additionalTaxRelief },
                { label: 'Total Reliefs', value: taxCalc.totalReliefs },
                { label: 'Corporation Tax Due', value: taxCalc.corporationTaxDue, emphasize: true },
                { label: 'Tax Paid', value: taxCalc.taxPaid },
                { label: 'Tax Owing or Overpaid', value: taxCalc.taxOwingOrOverpaid, emphasize: true },
                { label: 'Gross Corporation Tax', value: taxCalc.grossCorporationTax },
              ])}
              {Array.isArray(taxCalc.financialYears) && taxCalc.financialYears.length > 0 && (
                <div className='mt-4'>
                  <p className='text-sm font-semibold text-gray-800 mb-2'>Financial Years</p>
                  <div className='overflow-x-auto'>
                    <table className='min-w-full text-sm text-gray-700'>
                      <thead className='bg-gray-50 text-left text-xs font-semibold text-gray-500'>
                        <tr>
                          <th className='px-3 py-2'>Year</th>
                          <th className='px-3 py-2 text-right'>Profit</th>
                          <th className='px-3 py-2 text-right'>Tax Rate</th>
                          <th className='px-3 py-2 text-right'>Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxCalc.financialYears.map((fy: any, idx: number) => (
                          <tr key={idx} className='border-t border-gray-100'>
                            <td className='px-3 py-2'>{fy.year || '—'}</td>
                            <td className='px-3 py-2 text-right'>{formatMoney(fy.profit)}</td>
                            <td className='px-3 py-2 text-right'>{fy.taxRate ? `${fy.taxRate}%` : '—'}</td>
                            <td className='px-3 py-2 text-right'>{formatMoney(fy.tax)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Section>

            <Section title='Research & Development' subtitle={rAndD.title} defaultOpen={false}>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='SME' value={formatBool(rAndD.flags?.isSME)} />
                <Field label='Large Company' value={formatBool(rAndD.flags?.isLarge)} />
                <Field label='R&D Expenditure' value={formatMoney(rAndD.claim?.rdExpenditure)} />
                <Field label='R&D Enhancement' value={formatMoney(rAndD.claim?.rdEnhancement)} />
                <Field label='R&D Credit' value={formatMoney(rAndD.claim?.rdCredit)} />
                <Field label='R&D Restriction' value={formatMoney(rAndD.claim?.rdRestriction)} />
              </div>
            </Section>

            <Section title='Close Company' subtitle={closeCompany.title} defaultOpen={false}>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Is Close Company' value={formatBool(closeCompany.details?.isCloseCompany)} />
                <Field label='Benefits in Kind' value={formatMoney(closeCompany.details?.benefitsInKind)} />
                <Field label='Loans to Participators' value={formatMoney(closeCompany.details?.loansToParticipators)} />
                <Field
                  label='Participators'
                  value={
                    Array.isArray(closeCompany.participators) && closeCompany.participators.length > 0
                      ? closeCompany.participators.map((p: any) => p.name || JSON.stringify(p)).join(', ')
                      : 'None'
                  }
                  full
                />
              </div>
            </Section>

            <Section title='Supplementary Schedules' subtitle={sections.supplementarySchedules?.title} defaultOpen={false}>
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3'>
                {Object.entries(schedules).map(([key, value]) => (
                  <div key={key} className='flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2'>
                    <span className='text-sm font-semibold text-gray-800 uppercase'>{key}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border ${
                        value
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}
                    >
                      {value ? 'Yes' : 'No'}
                    </span>
                  </div>
                ))}
              </div>
            </Section>

            <Section title='Declaration' subtitle={sections.declaration?.title}>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4'>
                <Field label='Submitted By' value={declaration.submittedBy} />
                <Field label='Role' value={titleCase(declaration.submitterRole)} />
                <Field label='Declaration Accepted' value={formatBool(declaration.declarationAccepted)} />
                <Field label='Submission Date' value={formatDateTime(declaration.submissionDate)} />
                <Field label='Agent Reference' value={declaration.agentReference} />
                <Field label='Software Name' value={declaration.softwareName} />
                <Field label='Software Version' value={declaration.softwareVersion} />
              </div>
            </Section>

            {xmlAuditLog.length > 0 && (
              <Section title='XML Audit Log' subtitle={`${xmlAuditLog.length} entries`} defaultOpen={false}>
                <div className='space-y-3'>
                  {xmlAuditLog.map((entry, idx) => {
                    const isOpen = !!showXml[idx]
                    return (
                      <div key={entry._id || idx} className='rounded-xl border border-gray-100'>
                        <button
                          type='button'
                          onClick={() => setShowXml(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          className='w-full flex items-center justify-between gap-3 px-3 py-2 text-left'
                        >
                          <div className='min-w-0'>
                            <p className='text-sm font-semibold text-gray-800 truncate'>{titleCase(entry.type)}</p>
                            <p className='text-xs text-gray-500'>
                              {formatDateTime(entry.timestamp)}
                              {entry.periodIndex !== undefined && ` · Period #${entry.periodIndex}`}
                            </p>
                          </div>
                          {isOpen ? (
                            <ChevronDown className='h-4 w-4 text-gray-500 flex-shrink-0' />
                          ) : (
                            <ChevronRight className='h-4 w-4 text-gray-500 flex-shrink-0' />
                          )}
                        </button>
                        {isOpen && (
                          <pre className='bg-gray-50 border-t border-gray-100 px-3 py-3 text-[11px] leading-relaxed text-gray-800 overflow-x-auto max-h-96 whitespace-pre-wrap break-all'>
                            {entry.xml}
                          </pre>
                        )}
                      </div>
                    )
                  })}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  )
}
